import { UploadFile } from "@king-ict/eupisi-ui/models/UploadFile";

import { CommitteeEmployee } from "../Committee";
import { InstitutionProgram } from "../Institution";
import { Questionnaire, RequestAnswer } from "../Questionnaire";
import { HierarchicalCodeBook, PersonIdentification } from "../Shared";
import { LanguageCourseDecisionStatus } from "./LanguageCourseDecisionStatus";
import { OutsideEnrollmentAreaStatus } from "./OutsideEnrollmentAreaStatus";
import { PersonDetails } from "./PersonDetails";
import { RequestStatus } from "./RequestStatus";
import { RequestType } from "./RequestType";
import { SynchronizationStatus } from "./SynchronizationStatus";
import { Attachment } from "../Shared/Attachment";
import { RequestEnrollmentStatus } from "./RequestEnrollmentStatus";

export interface Request {
  id: number;
  code: string;
  requestUID: string;
  statusId: RequestStatus;
  type: RequestType;
  requestEnrollmentStatus?:
    | {
        id: RequestEnrollmentStatus;
        adminName?: string;
        publicName?: string;
      }
    | number;
  applicantParent?: PersonIdentification;
  applicantParentDetails?: PersonDetails;
  child?: PersonIdentification;
  childDetails?: PersonDetails;
  otherParentDetails: PersonDetails | null;
  schoolYearId: number;
  schoolYearCode: string | null;
  institutionProgramId: number | null;
  institutionProgram: InstitutionProgram | null;
  parentRemark: string | null;

  institutionId?: number;
  institutionName?: string;
  commercialProgramId: number | null;
  isOutsideEnrollmentArea: boolean;
  outsideEnrollmentAreaInstitutionProgramId: number | null;
  outsideEnrollmentAreaInstitutionProgram: InstitutionProgram | null;
  institutionChangeReason: InstitutionChangeReason | null;
  institutionChangeReasonOther: string | null;
  createdByAdmin: boolean;
  createdByAppUserId: number | null;
  outsideEnrollmentAreaCommercialProgramId: number | null;

  step: number;
  subStep: number | null;
  schoolCandidateId?: number;
  institutionCommitteeId?: number;
  countyCommitteeId?: number;
  countyId?: number;

  mandatoryForeignLanguages: HierarchicalCodeBook[] | null;
  electiveSubjects: HierarchicalCodeBook[] | null;
  languageSchoolingModels: HierarchicalCodeBook[] | null;
  foreignLanguages: HierarchicalCodeBook[] | null;

  questionnaire: Questionnaire | null;
  questionnaireAnswers: RequestAnswer[];

  childUploadFiles: UploadFile[] | null;
  childAttachments: Attachment[] | null;
  educationalRehabilitationUploadFiles: UploadFile[] | null;
  educationalRehabilitationAttachments: Attachment[] | null;
  medicalUploadFiles: UploadFile[] | null;
  medicalAttachments: Attachment[] | null;
  otherUploadFiles: UploadFile[] | null;
  otherAttachments: Attachment[] | null;
  parentUploadFiles: UploadFile[] | null;
  parentAttachments: Attachment[] | null;
  psychologyUploadFiles: UploadFile[] | null;
  psychologyAttachments: Attachment[] | null;
  psychophysicalConditionDecisionUploadFiles: UploadFile[] | null;
  psychophysicalConditionDecisionAttachments: Attachment[] | null;
  uniqueBodyOfExpertiseUploadFiles: UploadFile[] | null;
  uniqueBodyOfExpertiseAttachments: Attachment[] | null;
  subsequentUploadFiles: UploadFile[] | null;
  subsequentAttachments: Attachment[] | null;

  suggestedEnrollmentOutsideEnrollmentArea?: boolean;
  approvedEnrollmentOutsideEnrollmentArea?: boolean;
  outsideEnrollmentAreaStatus: OutsideEnrollmentAreaStatus | null;
  languageCourseDecisionStatus: LanguageCourseDecisionStatus | null;

  enrollmentInstitutionId: number | null;
  enrollmentInstitutionCountyId?: number | null;
  synchronizationStatus?: SynchronizationStatus;

  remarks: RequestRemark[] | null;

  statusHistory: StatusHistory[] | null;

  registrationFormDataChangeId: number | null;
  parentCountyId: number | null;
}

export interface StatusHistory {
  id: number;
  requestId: number;
  status: RequestStatus;
  note?: string;
  dateCreated: string;
}

export interface CommitteeDecision {
  id: number;
  requestId: number;
  committeeEmployeeId: number;
  committeeEmployee?: CommitteeEmployee;
  remark?: string;
  /**
   * This is no longer editable but it may be again in the future.
   */
  updatedDate: Date;
  committeeEnrollmentDecisionId: CommitteeEnrollmentDecision;
  committeeLanguageDecisionId?: CommitteeLanguageDecision;
  dateCreated: Date;
  dateUpdated?: Date;

  request?: Request;

  appUser?: AppUser;
  createdAppUser?: AppUser;

  // On the BE the value is sent as a nullable. So it will be true or null
  hasDecisionsInPreviousInstitutionCommittees: true | null;
  hasDecisionsInPreviousCountyCommittees: true | null;
}

export interface RequestRemark {
  id: number;
  requestId: number;
  remark: string | null;
  requestRemarkTypeId: number;
}

export interface AppUser {
  oib: string;
  firstName: string;
  lastName: string;
}

export enum CommitteeEnrollmentDecision {
  /// <summary>
  /// Spreman/spremna za upis<br/>
  /// Ready for enrollment
  /// </summary>
  Ready = 1,
  /// <summary>
  /// Nije spreman/spremna za upis<br/>
  /// Not ready for enrollment
  /// </summary>
  NotReady,
}

export enum CommitteeLanguageDecision {
  /// <summary>
  /// Nije potrebno uključivanje u pripremnu ili dopunsku nastavu hrvatskog jezika<br/>
  /// Supplementary or preparatory courses not required.
  /// </summary>
  NotRequired = 1,
  /// <summary>
  /// Potrebno uključivanje u pripremnu ili dopunsku nastavu hrvatskog jezika<br/>
  /// Supplementary or preparatory courses required.
  /// </summary>
  Required,
}

export enum InstitutionChangeReason {
  ExtendedStay = 1,
  FullDayClasses,
  DevelopmentalDisabilities,
  BarrierFree,
  NationalMinorityLanguage,
  Tenancy,
  Relocation,
  FamilySituation,
  Other,
  BrotherAndSistersAlreadyAssignedInSchool,
  AvailabilityOfPublicTransport,
  AllDaySchool,
}
